import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { Flag } from "lucide-react";
import PayoutDetails from "@/components/PayoutDetails";
import MyListings from "@/components/MyListings";
import WishlistList from "@/components/WishlistList";
import SellerVerification from "@/components/SellerVerification";
import SellerSales from "@/components/SellerSales";
import MyNegotiations from "@/components/MyNegotiations";
import ReportIssueModal from "@/components/ReportIssueModal";

export default function Account() {
  const { user, logout, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [thrifts, setThrifts] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [bargainUnread, setBargainUnread] = useState(0);
  const [tab, setTab] = useState(searchParams.get("tab") || "orders");
  const [reporting, setReporting] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/thrift/mine").then(({ data }) => setThrifts(data)).catch(() => {});
    api.get("/disputes/mine").then(({ data }) => setDisputes(data)).catch(() => {});
    api.get("/negotiations/unread-count").then(({ data }) => setBargainUnread(data.count || 0)).catch(() => {});
  }, [user, tab]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
  }, [searchParams, tab]);

  const changeTab = (k) => {
    setTab(k);
    setSearchParams({ tab: k });
  };

  if (authLoading) return <div className="p-16 text-center font-mono-b" data-testid="account-loading">LOADING...</div>;
  if (!user) return <Navigate to="/login" />;

  const kyc = user.kyc_status || "unsubmitted";
  const kycPill = { approved: "", pending: "orange", rejected: "pink" }[kyc] || "orange";

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="account-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="sticker pink">YOUR SPOT</span>
          <h1 className="font-display text-5xl md:text-6xl mt-2">WHAT'S UP, <span className="text-[#FF007F]">{user.name.split(" ")[0].toUpperCase()}</span></h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <p className="font-mono-b text-sm text-black/70">{user.email} · {user.auth_provider === "google" ? "Google" : "Email"} · {user.role.toUpperCase()}</p>
            <span className={`sticker text-[10px] ${kycPill}`}>KYC · {kyc.toUpperCase()}</span>
          </div>
        </div>
        <button onClick={logout} data-testid="account-logout" className="border-2 border-black bg-white px-4 py-2 font-mono-b font-bold uppercase brutal-shadow-sm hover-nudge">Log out</button>
      </div>

      <div className="flex gap-2 border-b-2 border-black mb-6 overflow-x-auto">
        {[
          ["orders","MY ORDERS"],
          ["thrifts","MY SUBMISSIONS"],
          ["listings","MY LISTINGS"],
          ["sales","MY SALES"],
          ["wishlist","WISHLIST"],
          ["bargains","BARGAINS"],
          ["verification","VERIFICATION"],
          ["disputes","DISPUTES"],
          ["payout","PAYOUT"],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`account-tab-${k}`}
            onClick={() => changeTab(k)}
            className={`px-4 py-2 font-mono-b font-bold uppercase border-2 border-b-0 border-black -mb-[2px] whitespace-nowrap ${tab === k ? "bg-black text-[#D4FF00]" : "bg-white"}`}
          >
            {l}
            {k === "bargains" && bargainUnread > 0 && (
              <span data-testid="bargains-unread-badge" className="ml-2 text-[10px] px-1.5 border-2 border-black bg-[#FF007F] text-white">{bargainUnread}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "payout" && <PayoutDetails />}
      {tab === "listings" && <MyListings />}
      {tab === "wishlist" && <WishlistList />}
      {tab === "verification" && <SellerVerification />}
      {tab === "sales" && <SellerSales />}
      {tab === "bargains" && <MyNegotiations />}

      {tab === "disputes" && (
        disputes.length === 0 ? (
          <div className="border-2 border-dashed border-black p-12 text-center font-mono-b" data-testid="account-no-disputes">
            No open reports. If something goes wrong with an order, you can file one from the "My Orders" tab.
          </div>
        ) : (
          <div className="space-y-3" data-testid="account-disputes">
            {disputes.map((d) => (
              <div key={d.dispute_id} className="bg-white border-2 border-black brutal-shadow-sm p-4" data-testid={`my-dispute-${d.dispute_id}`}>
                <div className="flex justify-between gap-2 flex-wrap">
                  <div>
                    <p className="font-mono-b text-xs text-black/60">Order {d.order_id?.slice(0, 16)}</p>
                    <p className="font-display text-lg">{d.reason?.replace("_", " ").toUpperCase()}</p>
                    <p className="font-mono-b text-xs text-black/70 mt-1">{d.description}</p>
                    {d.resolution && (
                      <p className="mt-2 font-mono-b text-[11px] bg-[#F4F4F0] border-2 border-black p-2">
                        Resolution: <b>{d.resolution.replace("_", " ")}</b>
                      </p>
                    )}
                  </div>
                  <span className={`sticker text-[10px] ${d.status === "resolved" ? "" : "pink"}`}>{d.status?.replace("_", " ").toUpperCase()}</span>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "orders" && (
        orders.length === 0 ? (
          <div className="border-2 border-dashed border-black p-12 text-center font-mono-b" data-testid="account-no-orders">
            No orders yet. <Link to="/shop" className="underline font-bold">Start shopping</Link>.
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.order_id} data-testid={`order-${o.order_id}`} className="bg-white border-2 border-black brutal-shadow-sm p-4">
                <div className="flex justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-mono-b text-xs text-black/60">{o.order_id.toUpperCase()}</p>
                    <p className="font-display text-2xl">₹{o.total} · <span className={`text-sm ${o.status === "paid" || o.status === "shipped" ? "text-green-700" : o.status === "refunded" ? "text-[#FF007F]" : "text-[#FF007F]"}`}>{o.status.toUpperCase()}</span></p>
                    {o.awb && (
                      <p className="font-mono-b text-xs mt-1">Tracking: <b>{o.courier} · {o.awb}</b></p>
                    )}
                  </div>
                  <p className="font-mono-b text-xs text-black/60">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-3">
                  {o.items.map((it) => (
                    <div key={`${it.product_id}-${it.size}-${it.color}`} className="flex gap-2 items-center border-2 border-black bg-[#F4F4F0] p-2">
                      {it.image && <img src={it.image} alt="" className="w-12 h-12 object-cover" />}
                      <div className="font-mono-b text-xs"><b>{it.name}</b><br/>×{it.quantity} {it.size ? `· ${it.size}` : ""}</div>
                    </div>
                  ))}
                </div>
                {o.coupon_code && o.discount > 0 && (
                  <div className="mt-3 border-2 border-black bg-[#D4FF00] px-3 py-2 inline-block" data-testid={`order-coupon-${o.order_id}`}>
                    <p className="font-mono-b text-xs">
                      <b>{o.coupon_code}</b> ({o.coupon_percent}% off) · you saved <b>₹{o.discount}</b>
                    </p>
                  </div>
                )}
                {(o.status === "paid" || o.status === "shipped") && !o.under_review && (
                  <div className="mt-3">
                    <button
                      onClick={() => setReporting(o)}
                      data-testid={`report-issue-${o.order_id}`}
                      className="border-2 border-black bg-white px-3 py-1 font-mono-b text-xs font-bold uppercase inline-flex items-center gap-1 hover-nudge"
                    >
                      <Flag className="w-3 h-3" /> REPORT AN ISSUE
                    </button>
                  </div>
                )}
                {o.under_review && (
                  <div className="mt-3 border-2 border-black bg-[#FF007F]/10 p-2">
                    <p className="font-mono-b text-xs"><b>UNDER REVIEW</b> · Our team is investigating your report.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {tab === "thrifts" && (
        thrifts.length === 0 ? (
          <div className="border-2 border-dashed border-black p-12 text-center font-mono-b" data-testid="account-no-thrifts">
            No submissions yet. <Link to="/sell" className="underline font-bold">Submit your first fit</Link>.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {thrifts.map((t) => (
              <div key={t.submission_id} data-testid={`thrift-${t.submission_id}`} className="bg-white border-2 border-black brutal-shadow-sm">
                {t.images?.[0] && <img src={t.images[0]} alt="" className="w-full aspect-[4/3] object-cover border-b-2 border-black" />}
                <div className="p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-display text-xl">{t.name}</h3>
                      <p className="font-mono-b text-xs text-black/60">{t.category.replace("-", " ")}</p>
                    </div>
                    <span className={`sticker text-[10px] ${t.status === "approved" ? "" : t.status === "rejected" ? "pink" : "orange"}`}>{t.status.toUpperCase()}</span>
                  </div>
                  <p className="font-mono-b font-bold mt-2">₹{t.price}</p>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {reporting && (
        <ReportIssueModal
          order={reporting}
          onClose={() => setReporting(null)}
          onSubmitted={() => api.get("/orders/mine").then(({ data }) => setOrders(data))}
        />
      )}
    </div>
  );
}
